import type { Feature, FeatureCollection, GeoJsonObject, Geometry, LineString, MultiLineString, Position } from 'geojson';
import { latLngBounds, type Map } from 'leaflet';

export type RiverLineCoordinate = [number, number];
export type RiverLinePath = RiverLineCoordinate[];

export interface RiverLineSelection {
  branchIndex: number;
  coordinateIndex: number;
}

export interface RiverDeviceMarker {
  id: string;
  name: string;
  type: string;
  areaName: string;
  coordinate: RiverLineCoordinate;
}

export function createRiverLineFeatureCollection(paths: RiverLinePath[]): FeatureCollection<LineString> {
  return {
    type: 'FeatureCollection',
    features: paths
      .filter((path) => path.length >= 2)
      .map((path, index) => {
        return {
          type: 'Feature',
          properties: {
            branchIndex: index,
          },
          geometry: {
            type: 'LineString',
            coordinates: path,
          },
        } satisfies Feature<LineString>;
      }),
  };
}

function normalizeCoordinates(positions: Position[]): RiverLinePath {
  return positions
    .filter((position): position is RiverLineCoordinate => Array.isArray(position) && position.length >= 2)
    .map((position) => [Number(position[0]), Number(position[1])]);
}

function extractPathsFromGeometry(geometry?: Geometry | null): RiverLinePath[] {
  if (!geometry) {
    return [];
  }

  if (geometry.type === 'LineString') {
    const path = normalizeCoordinates((geometry as LineString).coordinates);
    return path.length ? [path] : [];
  }

  if (geometry.type === 'MultiLineString') {
    return (geometry as MultiLineString).coordinates
      .map((positions) => normalizeCoordinates(positions))
      .filter((path) => path.length);
  }

  return [];
}

export function extractRiverLinePaths(geoJson?: GeoJsonObject | null): RiverLinePath[] {
  if (!geoJson) {
    return [];
  }

  if (geoJson.type === 'FeatureCollection') {
    const featureCollection = geoJson as FeatureCollection;
    return featureCollection.features.flatMap((feature) => extractPathsFromGeometry(feature.geometry));
  }

  if (geoJson.type === 'Feature') {
    const feature = geoJson as Feature;
    return extractPathsFromGeometry(feature.geometry);
  }

  return extractPathsFromGeometry(geoJson as Geometry);
}

export function flattenRiverLineCoordinates(paths: RiverLinePath[]) {
  return paths.flat();
}

export function getDefaultActiveRiverLineSelection(paths: RiverLinePath[]): RiverLineSelection | null {
  for (let branchIndex = paths.length - 1; branchIndex >= 0; branchIndex -= 1) {
    const path = paths[branchIndex];
    if (path?.length) {
      return {
        branchIndex,
        coordinateIndex: path.length - 1,
      };
    }
  }

  return null;
}

export function getTotalRiverPointCount(paths: RiverLinePath[]) {
  return paths.reduce((total, path) => total + path.length, 0);
}

export function stringifyRiverGeoJson(geoJson: GeoJsonObject) {
  return JSON.stringify(geoJson, null, 2);
}

export function fitMapToRiverLine(map: Map | null, paths: RiverLinePath[]) {
  const coordinates = flattenRiverLineCoordinates(paths);

  if (!map || !coordinates.length) {
    return;
  }

  if (coordinates.length === 1) {
    map.setView([coordinates[0][1], coordinates[0][0]], 13);
    return;
  }

  map.fitBounds(
    latLngBounds(
      coordinates.map(([lng, lat]) => {
        return [lat, lng] as [number, number];
      }),
    ),
    {
      padding: [32, 32],
      maxZoom: 15,
    },
  );
}

export function createRiverGeoJsonFilename(riverName: string) {
  const sanitizedRiverName = riverName.trim().replace(/[\\/:*?"<>|\s]+/g, '_').replace(/^_+|_+$/g, '');
  return `${sanitizedRiverName || 'river'}.geojson`;
}

export function parseDeviceCoordinate(value?: string | null): RiverLineCoordinate | null {
  if (!value) {
    return null;
  }

  const [latRaw, lngRaw] = value.split(',');
  const lat = Number(latRaw);
  const lng = Number(lngRaw);

  if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
    return null;
  }

  return [lng, lat];
}

export function isHydroVenusDevice(type?: string | null, name?: string | null) {
  const normalized = `${type || ''} ${name || ''}`.toLowerCase();
  return normalized.includes('hydro') || normalized.includes('venus');
}
